// Place an Order → CNC (Countertop / Cabinet) ordering inside the ordering-system iframe.
//
// Flooring loads `customer.crossDomainLink` (select-plan → property-options) + `uniqueChannelName`
// in an iframe (see CustomerList.handlePropertyClickForOrdering). CNC reuses that exact URL — same
// login hash, contact id and channel — and only adds `orderType=cnc`, which makes the ordering
// app's property-options.html open its CNC unit modal instead of the Flooring one.
//
// When a CNC order is finished, the iframe posts CNC_ORDER_COMPLETE_MESSAGE to this window
// (ordering-system js/customJS/cnc/cncConfirmation.js).

export const ORDER_TYPE_FLOORING = "flooring";
export const ORDER_TYPE_CNC = "cnc";
export const CNC_ORDER_COMPLETE_MESSAGE = "geoff-cnc-order-complete";
// Sent when an iframe opened as Flooring continues as a CNC order (a CNC unit that isn't set up for
// Flooring — ordering-system propertyList.js → tryStartCncOrder).
export const CNC_ORDER_STARTED_MESSAGE = "geoff-cnc-order-started";

// True for a CNC_ORDER_STARTED_MESSAGE from the ordering origin.
export function isCncOrderStartedMessage(event, expectedOrigin) {
  return !!event && !!expectedOrigin && event.origin === expectedOrigin &&
    !!event.data && typeof event.data === "object" && event.data.type === CNC_ORDER_STARTED_MESSAGE;
}

// Add orderType=cnc to the Flooring ordering URL, keeping every other parameter as-is.
export function buildCncOrderingUrl(orderingUrl) {
  if (!orderingUrl) return orderingUrl;
  try {
    const url = new URL(orderingUrl, window.location.href);
    url.searchParams.set("orderType", ORDER_TYPE_CNC);
    return url.toString();
  } catch (e) {
    return orderingUrl + (orderingUrl.includes("?") ? "&" : "?") + "orderType=" + ORDER_TYPE_CNC;
  }
}

// Optional local-development override: load the ordering iframe from another origin (e.g. a local
// ordering-system on http://localhost:3030) instead of crossDomainLink's deployed host. Set
// REACT_APP_ORDERING_ORIGIN in .env.development.local; when unset (all deployments) the URL is untouched.
export function withOrderingOriginOverride(orderingUrl, overrideOrigin = process.env.REACT_APP_ORDERING_ORIGIN) {
  if (!orderingUrl || !overrideOrigin) return orderingUrl;
  try {
    const url = new URL(orderingUrl);
    const target = new URL(overrideOrigin);
    return target.origin + url.pathname + url.search + url.hash;
  } catch (e) {
    return orderingUrl;
  }
}

// A Countertop / Cabinet property (GetCustomerInfo `isCnCProperty`).
export function isCncProperty(customer) {
  return customer?.isCnCProperty === true || customer?.isCnCProperty === "true";
}

// Iframe URL for Place an Order: CnC properties open the CNC flow (no Flooring unit-mix / floor plan
// check); every other property — and every quote, which is Flooring-only — uses the Flooring URL
// unchanged.
export function orderingUrlForProperty(customer, flooringUrl, { quoteMode = false } = {}) {
  if (!quoteMode && isCncProperty(customer)) {
    return { orderType: ORDER_TYPE_CNC, url: buildCncOrderingUrl(flooringUrl) };
  }
  return { orderType: ORDER_TYPE_FLOORING, url: flooringUrl };
}

// Origin of the ordering app, used to accept postMessage only from the iframe we opened.
export function getOrderingOrigin(orderingUrl) {
  try {
    return new URL(orderingUrl, window.location.href).origin;
  } catch (e) {
    return null;
  }
}

// Validate a message event from the CNC iframe. Returns { contactInfoId, unitId, jobNumbers } or null.
export function parseCncOrderCompleteMessage(event, expectedOrigin) {
  if (!event || !expectedOrigin || event.origin !== expectedOrigin) return null;
  const data = event.data;
  if (!data || typeof data !== "object" || data.type !== CNC_ORDER_COMPLETE_MESSAGE) return null;
  const jobNumbers = (Array.isArray(data.jobNumbers) ? data.jobNumbers : [])
    .map((j) => String(j ?? "").trim())
    .filter(Boolean);
  return {
    contactInfoId: data.contactInfoId ?? "",
    unitId: data.unitId ?? "",
    unitName: data.unitName ?? "",
    jobNumbers,
  };
}

// SaveCnCOrder sends "update-order-status" with PusherTrigger "cnc-order-status", UnitId and JobNum
// once per created order (one per selected area). Events arriving within `windowMs` of the previous
// one belong to the same save and are accumulated so the ERP can show the right place for all of them.
export const CNC_PUSHER_BATCH_WINDOW_MS = 15000;

export function collectCncPusherOrder(previous, data, now = Date.now(), windowMs = CNC_PUSHER_BATCH_WINDOW_MS) {
  const jobNum = String(data?.JobNum ?? "").trim();
  const sameBatch = previous && now - previous.at <= windowMs;
  const jobNumbers = sameBatch ? [...previous.jobNumbers] : [];
  if (jobNum && !jobNumbers.includes(jobNum)) jobNumbers.push(jobNum);
  return {
    at: now,
    unitId: data?.UnitId ?? (sameBatch ? previous.unitId : ""),
    jobNumbers,
  };
}

// True while a CNC save's Pusher events may still be arriving (follow-up events of a multi-area order
// must not be mistaken for a Flooring order once the popup has closed).
export function isWithinCncPusherBatch(previous, now = Date.now(), windowMs = CNC_PUSHER_BATCH_WINDOW_MS) {
  return !!previous && now - previous.at <= windowMs;
}

// Where to go after a CNC order: its detail page when exactly one order was created (one per
// selected area), otherwise the CNC orders list.
export function cncOrderCompletePath({ contactInfoId, unitId, jobNumbers = [] } = {}) {
  if (jobNumbers.length === 1 && contactInfoId && unitId) {
    return `/cnc-estimate/contactInfoId=${encodeURIComponent(contactInfoId)}/unitId=${encodeURIComponent(unitId)}/jobNum=${encodeURIComponent(jobNumbers[0])}`;
  }
  return "/cnc-orders";
}
